/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: AnnouncementsPage.tsx
 ??? Created: 2025-01-28
 ?? Description:
    Announcements list page with table view, search, pagination, and actions.
    Allows viewing, creating, duplicating, editing, and deleting announcements.

 ?? Notes:
    - Domain-scoped (multi-tenant safe)
    - Supports search and pagination
    - Shows name, destination, status, and recording details

 ?? Last Updated: 2025-11-07 by GPT-5 Codex
----------------------------------------------------------------*/

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, Edit, Trash2, Volume2 } from 'lucide-react';
import { ModulePageContainer } from '../../components/layouts';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import DataGridSimple from '../../components/ui/DataGridSimple';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { useNavigationStore } from '../../store/navigation';
import { deleteAnnouncementById, navigateToAnnouncementDuplicate } from './announcementsActions';
import { useAnnouncementList } from './hooks/useAnnouncementList';
import { Announcement } from './types';

const ACTION_BUTTON_CLASSNAMES = {
  base: 'p-2 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60',
  edit: 'hover:bg-blue-100 dark:hover:bg-blue-900/30',
  duplicate: 'hover:bg-emerald-100 dark:hover:bg-emerald-900/30',
  delete: 'hover:bg-red-100 dark:hover:bg-red-900/30',
};


export default function AnnouncementsPage() {
  const { t } = useI18n();
  const { setCurrentPageTitle } = useNavigationStore();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotifications();

  const pageTitle = useMemo(() => t('announcements.title'), [t]);

  useEffect(() => {
    setCurrentPageTitle(null);
    return () => setCurrentPageTitle(null);
  }, [setCurrentPageTitle]);

  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; announcement: Announcement | null }>(
    { isOpen: false, announcement: null },
  );
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    data,
    loading,
    columns: baseColumns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  } = useAnnouncementList();

  // Add custom renders to columns
  const columns = useMemo(
    () =>
      baseColumns.map((column) => {
        if (column.key === 'name') {
          return {
            ...column,
            render: (_value: unknown, item: Announcement) => (
              <div className="font-medium text-foreground">{item.name}</div>
            ),
          };
        }
        if (column.key === 'recording') {
          return {
            ...column,
            render: (_value: unknown, item: Announcement) => (
              <span className="text-sm text-muted-foreground">
                {item.recordingName || `ID: ${item.recordingId}`}
              </span>
            ),
          };
        }
        if (column.key === 'destination') {
          return {
            ...column,
            render: (_value: unknown, item: Announcement) => {
              // Helper function to translate destination
              const translateDestination = (module: string | undefined, value: string | undefined) => {
                if (!module) return null;
                
                // Translate module name
                const moduleLabel = t(`outboundRoutes.modules.${module}.label`, { defaultValue: module });
                
                // For terminate_call, translate the cause code
                if (module === 'terminate_call' && value) {
                  const causeLabel = t(`outboundRoutes.terminateCauses.${value}.label`, { defaultValue: value });
                  return `${moduleLabel} � ${causeLabel}`;
                }
                
                // For other modules, show the value as-is
                return value ? `${moduleLabel} � ${value}` : moduleLabel;
              };

              const destination = translateDestination(item.destinationModule, item.destinationData);

              return (
                <div className="text-sm">
                  {destination ? (
                    <div className="font-medium text-foreground">{destination}</div>
                  ) : (
                    <div className="text-muted-foreground">{t('common:notAvailable')}</div>
                  )}
                </div>
              );
            },
          };
        }
        if (column.key === 'enabled') {
          return {
            ...column,
            render: (_value: unknown, item: Announcement) => (
              <span
                className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                  item.enabled
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300'
                }`}
              >
                {item.enabled ? t('common:enabled') : t('common:disabled')}
              </span>
            ),
          };
        }
        if (column.key === 'createdAt') {
          return {
            ...column,
            render: (_value: unknown, item: Announcement) => {
              const date = new Date(item.createdAt);
              const isValid = !Number.isNaN(date.getTime());
              return (
                <span className="text-sm text-muted-foreground">
                  {isValid ? date.toLocaleDateString() : t('common:notAvailable')}
                </span>
              );
            },
          };
        }
        return column;
      }),
    [baseColumns, t],
  );

  const handleCreate = () => {
    navigate('/pbx/incoming-tools/announcements/new');
  };

  const handleEdit = (announcement: Announcement) => {
    navigate(`/pbx/incoming-tools/announcements/${announcement.uuid ?? announcement.id}`);
  };

  const handleDuplicate = (announcement: Announcement) => {
    navigateToAnnouncementDuplicate(navigate, announcement.uuid ?? announcement.id);
  };

  const handleDeleteClick = useCallback((announcement: Announcement) => {
    setDeleteModal({ isOpen: true, announcement });
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteModal.announcement) return;

    setIsDeleting(true);
    try {
      await deleteAnnouncementById(deleteModal.announcement.id);
      showSuccess(t('announcements.messages.deleteSuccess'));
      setDeleteModal({ isOpen: false, announcement: null });
      refresh();
    } catch (error) {
      showError(error.message || t('announcements.messages.deleteError'));
    } finally {
      setIsDeleting(false);
    }
  }, [deleteModal.announcement, refresh, showError, showSuccess, t]);

  const renderActions = useCallback(
    (announcement: Announcement) => (
      <div className="flex items-center gap-2">
        <SimpleTooltip content={t('common:edit')} position="top">
          <button
            type="button"
            onClick={() => handleEdit(announcement)}
            aria-label={t('common:edit')}
            className={`${ACTION_BUTTON_CLASSNAMES.base} ${ACTION_BUTTON_CLASSNAMES.edit}`}
          >
            <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          </button>
        </SimpleTooltip>
        <SimpleTooltip content={t('common:duplicate')} position="top">
          <button
            type="button"
            onClick={() => handleDuplicate(announcement)}
            aria-label={t('common:duplicate')}
            className={`${ACTION_BUTTON_CLASSNAMES.base} ${ACTION_BUTTON_CLASSNAMES.duplicate}`}
          >
            <Copy className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
          </button>
        </SimpleTooltip>
        <SimpleTooltip content={t('common:delete')} position="top">
          <button
            type="button"
            onClick={() => handleDeleteClick(announcement)}
            aria-label={t('common:delete')}
            className={`${ACTION_BUTTON_CLASSNAMES.base} ${ACTION_BUTTON_CLASSNAMES.delete}`}
          >
            <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
          </button>
        </SimpleTooltip>
      </div>
    ),
    [handleDeleteClick, handleDuplicate, handleEdit, t],
  );

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <>
          <DataGridSimple
            data={data}
            columns={columns.map((column) => ({
              ...column,
              render:
                column.key === 'actions'
                  ? (_value, item: Announcement) => renderActions(item)
                  : column.render,
            }))}
            loading={loading}
            pagination={pagination}
            searchQuery={searchQuery}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSearch={handleSearch}
            onSort={handleSort}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            onColumnToggle={handleColumnToggle}
            onAdd={handleCreate}
            title={pageTitle}
            titleIcon={<Volume2 className="h-5 w-5 text-primary" />}
            addButtonText={t('announcements.actions.create')}
            searchPlaceholder={t('announcements.searchPlaceholder')}
            emptyMessage={t('announcements.noResults')}
          />

          <ConfirmationModal
            isOpen={deleteModal.isOpen}
            onClose={() => setDeleteModal({ isOpen: false, announcement: null })}
            onConfirm={handleDeleteConfirm}
            title={t('announcements.messages.deleteTitle')}
            message={t('announcements.messages.confirmDelete', {
              name: deleteModal.announcement?.name ?? '',
            })}
            confirmText={t('common:delete')}
            cancelText={t('common:cancel')}
            type="danger"
            isLoading={isDeleting}
          />
        </>
      }
      formView={null}
    />
  );
}

